import { useQuery } from "@apollo/client/react"
import { DateTime } from "luxon"
import { useEffect, useMemo, useState } from "react"

import { useLocale } from "@/contexts/locale"
import { GetPublicTableBusyBlocksByDateDocument } from "@/graphql/graphql"

import {
  DEFAULT_BEST_PICKS_LIMIT,
  getReservationBestPicks,
  type ReservationBestPick,
} from "../../../reservation_best_picks"
import { formatDurationHoursLabel, RESERVATION_DURATION_STEP_SECONDS } from "../../../reservation_duration_options"
import { getSidebarOpeningRange } from "../../../reservation_sidebar_availability"
import { findFreeGaps, getAvailableTableIds, getRoundedBookingThreshold } from "../../../reservation_table_availability"
import { type ReservationDetailsStepProps, useReservationDetailsState } from "../shared"

/** Which reservation dimension the guest committed to first (drives the "gravity-left" pill order). */
export type SelectionDimension = "time" | "table"

type UseMobileTableSelectionProps = Pick<
  ReservationDetailsStepProps,
  | "tables"
  | "openingHours"
  | "timezone"
  | "venueSlug"
  | "minAdvanceBookingTime"
  | "minReservationTime"
  | "maxReservationTime"
  | "venuePlans"
  | "editMode"
  | "originalReservation"
  | "onNext"
  | "onError"
>

const overlaps = (aStart: DateTime, aEnd: DateTime, bStart: string, bEnd: string): boolean => {
  const start = DateTime.fromISO(bStart)
  const end = DateTime.fromISO(bEnd)
  return start < aEnd && end > aStart
}

export const useMobileTableSelection = ({
  tables,
  openingHours,
  timezone,
  venueSlug,
  minAdvanceBookingTime,
  minReservationTime,
  maxReservationTime,
  venuePlans,
  editMode = false,
  originalReservation,
  onNext,
  onError,
}: UseMobileTableSelectionProps) => {
  const { locale } = useLocale()

  const {
    values,
    setFieldValue,
    isChecking,
    hasConflicts,
    setHasConflicts,
    scheduleTimeOptions,
    closedUntil,
    isValid,
    handleNext,
    durationDisplay,
  } = useReservationDetailsState({
    tables,
    openingHours,
    timezone,
    venueSlug,
    minAdvanceBookingTime,
    venuePlans,
    editMode,
    originalReservation,
    onNext,
    onError,
  })

  const hasTime = Boolean(values.startTime && values.endTime)
  const hasTable = Boolean(values.tableId)

  // Formik records the values but not the ORDER they were chosen; the pill layout needs the order,
  // so we track the first-committed dimension locally and keep it in sync with the live values.
  const [firstDimension, setFirstDimension] = useState<SelectionDimension | null>(() => {
    if (hasTime) return "time"
    if (hasTable) return "table"
    return null
  })

  useEffect(() => {
    setFirstDimension((prev) => {
      if (!hasTime && !hasTable) return null
      if (hasTime && !hasTable) return "time"
      if (hasTable && !hasTime) return "table"
      // Both set: keep the remembered first dimension (seed to "time" for a fresh both-set arrival).
      return prev ?? "time"
    })
  }, [hasTime, hasTable])

  const dateIso = values.date?.toISODate() || ""
  const { data: busyBlocksData } = useQuery(GetPublicTableBusyBlocksByDateDocument, {
    skip: !dateIso,
    variables: { slug: venueSlug, date: dateIso },
  })

  const allBusyBlocks = useMemo(() => busyBlocksData?.public?.venue?.tableBusyBlocksByDate ?? [], [busyBlocksData])

  const availableTableIds = useMemo(
    () =>
      getAvailableTableIds({
        tables,
        partySize: values.peopleAmount,
        date: values.date,
        openingHours,
        timezone,
        busyBlocks: allBusyBlocks,
        startTime: values.startTime || null,
        endTime: values.endTime || null,
        minReservationTime,
        minAdvanceBookingTime,
      }),
    [
      tables,
      values.peopleAmount,
      values.date,
      values.startTime,
      values.endTime,
      openingHours,
      timezone,
      allBusyBlocks,
      minReservationTime,
      minAdvanceBookingTime,
    ],
  )

  const availableCount = availableTableIds.size

  // Unavailable tables dim + become unclickable on the map — except the currently-selected table,
  // which must stay blue/selectable even if a later time edit conflicts with it (collision state).
  const disabledTables = useMemo(() => {
    const result: Record<string, string> = {}
    tables.forEach((table) => {
      if (!availableTableIds.has(table.id) && table.id !== values.tableId) result[table.id] = ""
    })
    return result
  }, [tables, availableTableIds, values.tableId])

  const freeTableIds = useMemo(() => Array.from(availableTableIds), [availableTableIds])

  // Exact-minute opening bounds (e.g. 17:30 / 23:30) for the date. The shared engine's timing state is
  // whole-hour granular (`getOpeningRange`), so thread these into the time picker's start/close bounds
  // and the timeline strip range so non-:00 venues don't show pre-open rows / early-capped ends.
  const openingRange = useMemo(
    () => getSidebarOpeningRange(values.date, openingHours, timezone),
    [values.date, openingHours, timezone],
  )

  const selectedTable = useMemo(
    () => (values.tableId ? tables.find((table) => table.id === values.tableId) ?? null : null),
    [values.tableId, tables],
  )

  const selectedTimeRange = useMemo(() => {
    if (!hasTime) return null
    // `setZone: true` keeps the venue offset carried by the ISO string; a plain parse would convert
    // to the device zone and mislabel the pill for guests whose device tz ≠ venue tz.
    const start = DateTime.fromISO(values.startTime, { setZone: true })
    const end = DateTime.fromISO(values.endTime, { setZone: true })
    return `${start.toFormat("HH:mm")} - ${end.toFormat("HH:mm")}`
  }, [hasTime, values.startTime, values.endTime])

  const maxDurationLabel = useMemo(() => {
    if (!maxReservationTime || maxReservationTime <= 0) return null
    return formatDurationHoursLabel(Math.round(maxReservationTime / 60), locale)
  }, [maxReservationTime, locale])

  const selectedTableBusyBlocks = useMemo(() => {
    if (!values.tableId) return []

    const originalWindow =
      editMode && originalReservation && originalReservation.tableId === values.tableId
        ? {
            start: DateTime.fromISO(originalReservation.startTime),
            end: DateTime.fromISO(originalReservation.endTime),
          }
        : null

    return allBusyBlocks
      .filter((block) => block.tableId === values.tableId)
      .filter(
        (block) =>
          !originalWindow || !overlaps(originalWindow.start, originalWindow.end, block.startTime, block.endTime),
      )
      .map((block) => ({ startTime: block.startTime, endTime: block.endTime }))
  }, [allBusyBlocks, values.tableId, editMode, originalReservation])

  // Table chosen but no confirmed time: propose that table's earliest free window (capped at the max
  // reservation duration) as the green block. `acceptProposed` (the `+` handle) commits it to Formik.
  const proposedWindow = useMemo(() => {
    if (!values.tableId || hasTime) return null

    if (!openingRange) return null

    const bookingThreshold = minAdvanceBookingTime
      ? getRoundedBookingThreshold(DateTime.now().setZone(timezone).plus({ seconds: minAdvanceBookingTime }))
      : DateTime.now().setZone(timezone)
    const lower = bookingThreshold > openingRange.opensAt ? bookingThreshold : openingRange.opensAt
    const upper = openingRange.closesAt

    const minDurationSeconds =
      minReservationTime && minReservationTime > 0 ? minReservationTime : RESERVATION_DURATION_STEP_SECONDS

    const busyIntervals = selectedTableBusyBlocks.map((block) => ({
      start: DateTime.fromISO(block.startTime),
      end: DateTime.fromISO(block.endTime),
    }))

    const gap = findFreeGaps(lower, upper, busyIntervals).find(
      (freeGap) => freeGap.end.diff(freeGap.start, "seconds").seconds >= minDurationSeconds,
    )
    if (!gap) return null

    const maxDurationSeconds =
      maxReservationTime && maxReservationTime > 0 ? maxReservationTime : gap.end.diff(gap.start, "seconds").seconds
    const end = gap.start.plus({ seconds: maxDurationSeconds })

    return { start: gap.start, end: end < gap.end ? end : gap.end }
  }, [
    values.tableId,
    hasTime,
    openingRange,
    timezone,
    minAdvanceBookingTime,
    minReservationTime,
    maxReservationTime,
    selectedTableBusyBlocks,
  ])

  const acceptProposed = () => {
    if (!proposedWindow) return
    void setFieldValue("startTime", proposedWindow.start.toISO())
    void setFieldValue("endTime", proposedWindow.end.toISO())
  }

  // Empty-state "Best available picks": for each capacity-matching table, its earliest free window
  // (reuses the already-fetched busy blocks — no refetch). Only computed while nothing is selected.
  const bestPicks = useMemo<ReservationBestPick[]>(() => {
    if (hasTime || hasTable) return []
    return getReservationBestPicks({
      tables,
      partySize: values.peopleAmount,
      date: values.date,
      openingHours,
      busyBlocks: allBusyBlocks,
      timezone,
      minReservationTime,
      maxReservationTime,
      minAdvanceBookingTime,
      limit: DEFAULT_BEST_PICKS_LIMIT,
    })
  }, [
    hasTime,
    hasTable,
    tables,
    values.peopleAmount,
    values.date,
    openingHours,
    allBusyBlocks,
    timezone,
    minReservationTime,
    maxReservationTime,
    minAdvanceBookingTime,
  ])

  // A best-pick commits table + time together. We canonically treat "table" as the first dimension so
  // the table pill lands on the LEFT (gravity-left, matching Panel D — "table chosen first").
  const acceptPick = ({ tableId, startTime, endTime }: { tableId: string; startTime: string; endTime: string }) => {
    setFirstDimension("table")
    void setFieldValue("tableId", tableId)
    void setFieldValue("startTime", startTime)
    void setFieldValue("endTime", endTime)
  }

  // Commit an edited [start, end] window from the two-column time picker. The gravity-left order is
  // resolved by the `firstDimension` sync effect: time becomes first if no table was chosen, else the
  // remembered "table" first is preserved (time lands on the right).
  const editTime = (startTime: string, endTime: string) => {
    void setFieldValue("startTime", startTime)
    void setFieldValue("endTime", endTime)
  }

  const clearTime = () => {
    void setFieldValue("startTime", "")
    void setFieldValue("endTime", "")
  }

  const clearTable = () => {
    void setFieldValue("tableId", "")
    // The conflict signal is owned by the timeline, which unmounts when the table is cleared and so
    // never resets it. Clearing the table resets that dimension → reset the stale conflict here too,
    // otherwise the surviving time pill would stay red in the time-only state.
    setHasConflicts(false)
  }

  const selectTable = (tableId: string) => {
    void setFieldValue("tableId", tableId)
  }

  return {
    values,
    hasTime,
    hasTable,
    firstDimension,
    isChecking,
    hasConflicts,
    setHasConflicts,
    isValid,
    handleNext,
    scheduleTimeOptions,
    closedUntil,
    // display
    selectedTable,
    selectedTimeRange,
    durationDisplay,
    maxDurationLabel,
    availableCount,
    // map
    freeTableIds,
    disabledTables,
    selectedTableBusyBlocks,
    proposedWindow,
    openingRange,
    // availability lookup for the dropdown rows
    availableTableIds,
    // empty-state best picks
    bestPicks,
    // actions
    editTime,
    clearTime,
    clearTable,
    selectTable,
    acceptProposed,
    acceptPick,
  }
}
